/* istanbul ignore file */
import React from 'react';
import { batch } from 'react-redux';
import { useCurator } from '@noths/curator-web';
import { parseLocaleSetCookieHeaders } from '@noths/polaris-client-localisation';
import { NotFound } from '@noths/polaris-client-next-components';
import { DepartmentBanner } from '@noths/polaris-client-ribbons-design-system';
import { getMvtStrategyFromRequest } from '@noths/polaris-client-user-configuration';
import { generateRandomString } from '@noths/polaris-client-utils';
import { type CurrencyCode, StatusCode } from '@noths/polaris-dev-ts-types';
import type { GetServerSideProps } from 'next';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import { parse } from 'url';

import { adaptPageSettingsToStore } from 'src/adapters/contentToStore/adaptPageSettingsToStore';
import { adaptPageURLToStore } from 'src/adapters/pageURLToStore/adaptPageURLToStore';
import { SHOP_TAXONOMY_ATTRIBUTE_NAMES } from 'src/adapters/pageURLToStore/shop/constants';
import {
  levelsToPageCategories,
  mergeShopPathIntoQuery,
} from 'src/adapters/pageURLToStore/shop/shopTaxonomyPath';
import type { TaxonomyListingOwnProps } from 'src/components/layouts/TaxonomyListing/TaxonomyListing';
import { TaxonomyListingContainer } from 'src/components/layouts/TaxonomyListing/TaxonomyListingContainer';
import { isProd } from 'src/environment';
import { getFeatureFlagsWithMatchers } from 'src/featureFlags/getFeatureFlagsWithMatchers';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import { requestProducts } from 'src/redux/products/thunks/requestProducts';
import { PRODUCTS_LIST_DATA_FETCHING_ERROR } from 'src/redux/products/thunks/thunkErrors';
import { getInitialListingsControlsState } from 'src/redux/productsQuery/helpers/getInitialListingsControlsState';
import { applyShopTaxonomyToFacets } from 'src/redux/productsQuery/helpers/shop/applyShopTaxonomyToFacets';
import { selectPagePropsForClientHydration } from 'src/redux/selectors';
import { getStore } from 'src/redux/store';
import { actions as userActions } from 'src/redux/user/slice';
import { fetchGlobalContent } from 'src/services/contentstack/fetchGlobalContent';
import { fetchListingsControlsConfig } from 'src/services/contentstack/fetchListingsControlsConfig';
import { fetchPLPSettings } from 'src/services/contentstack/fetchPLPSettings';
import { getContentstackEnvironment } from 'src/services/contentstack/getContentstackEnvironment';
import { getCategoryServingConfig } from 'src/services/listings-api/servingConfigs';
import { fetchDeliveryEvent } from 'src/services/mononoth/fetchDeliveryEvent';
import { PageType } from 'src/types/navigation';
import type { NextConfig } from 'src/types/nextConfig';
import { getPageFromParam } from 'src/utils/queryParams';
import { logger } from 'src/utils/serverLogger';

const Curator = dynamic(
  () =>
    import('@noths/curator-web').then(async (m) => {
      m.configureCurator({
        env: isProd() ? 'production' : 'qa',
        hideChatToggleForMobileViewport: true,
      });
      return m.Curator;
    }),
  { ssr: false },
);

const { serverRuntimeConfig } = getConfig() as NextConfig;

export type ShopTaxonomyPageProps = TaxonomyListingOwnProps & {
  enableCurator?: boolean;
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query, req, res } = context;
  const preview = false;
  const featureFlags = getFeatureFlagsWithMatchers(req);
  const pathname = parse(req.url!).pathname!;
  const env = getContentstackEnvironment(preview);
  const { _ga: googleAnalyticsVisitorId, cognitoId, currency, delivery_zone } = req.cookies;
  const { currency: initialCurrency, deliveryZone: initialDeliveryZone } =
    parseLocaleSetCookieHeaders(res.getHeaders());
  const currencyCode = (initialCurrency ?? currency) as CurrencyCode;
  const gaVisitorId = googleAnalyticsVisitorId ?? generateRandomString(10);

  const [[siteChromeContent], settings, deliveryEvent, fetchedListingsControlsConfig] =
    await Promise.all([
      fetchGlobalContent({ featureFlags, urlPath: pathname }),
      fetchPLPSettings({ env }),
      fetchDeliveryEvent(),
      fetchListingsControlsConfig(),
    ]);

  if (!fetchedListingsControlsConfig) {
    logger.error('shop PLP: listings controls config unavailable');
    return { notFound: true };
  }

  const mergedPath = mergeShopPathIntoQuery(pathname, query);

  if ('error' in mergedPath) {
    return { notFound: true };
  }

  const { mergedQuery, shopTaxonomyLevels } = mergedPath;
  const queryParams = adaptPageURLToStore(mergedQuery);

  const { facets: configFacets, sortOptions } = getInitialListingsControlsState(
    fetchedListingsControlsConfig,
    {
      deliveryEvent: deliveryEvent && {
        date: deliveryEvent.last_deliver_by,
        name: deliveryEvent.name,
      },
    },
  );

  const taxonomyAttributes = [...SHOP_TAXONOMY_ATTRIBUTE_NAMES];
  const facets = applyShopTaxonomyToFacets(configFacets, shopTaxonomyLevels, taxonomyAttributes);
  const pageCategories = levelsToPageCategories(shopTaxonomyLevels);
  const servingConfig = getCategoryServingConfig(featureFlags);

  const { dispatch, getState } = getStore(
    {
      router: { pathname, query },
    },
    {
      content: {
        ...(settings ? adaptPageSettingsToStore(settings, pathname) : {}),
      },
      navigation: {
        pageType: PageType.Shop,
      },
      productsQuery: {
        canonicalFilters: { and: [] },
        facets,
        page: getPageFromParam(queryParams),
        pageCategories,
        ...(servingConfig && { servingConfig }),
        sortOptions,
        taxonomyAttributes,
      },
      user: {
        bot: getMvtStrategyFromRequest(req) === 'bot',
        cognitoID: cognitoId || null,
        currencyCode,
        gaVisitorId,
      },
    },
  );

  batch(() => {
    dispatch(navigationActions.setPathname({ url: req.url ?? '', host: req.headers.host ?? '' }));
    dispatch(userActions.setDeliveryZone(initialDeliveryZone ?? delivery_zone));
    dispatch(userActions.setFeatureFlags(featureFlags));
  });

  await dispatch(requestProducts({}));

  const state = getState();
  const { products } = state;

  if (products.error) {
    switch (products.error.name) {
      case PRODUCTS_LIST_DATA_FETCHING_ERROR.name:
        res.statusCode = StatusCode.NotFound;
        break;
      default:
        res.statusCode = StatusCode.InternalServerError;
        res.setHeader('Retry-After', serverRuntimeConfig.retryAfterDuration);
        break;
    }
  }

  const props: Partial<ShopTaxonomyPageProps> = {
    enableCurator: featureFlags?.curator_web === 'enabled' ?? false,
    featureFlags,
    preview,
    siteChromeContent,
    stickyTopBanner: siteChromeContent ? siteChromeContent.topBannerMessages.length > 0 : false,
    ...selectPagePropsForClientHydration(state),
  };

  return {
    props,
  };
};

const ShopListingPage = (props: ShopTaxonomyPageProps) => {
  const { enableCurator, ...taxonomyContainerProps } = props;
  const { content } = taxonomyContainerProps.updatedReduxState as ReduxApplicationState;

  const { handleToggle, isCuratorOpen, redirectToPDP } = useCurator();
  const showCurator = enableCurator ?? false;

  return (
    <>
      {content.departmentBanner && <DepartmentBanner {...content.departmentBanner} />}
      {content.error ? <NotFound /> : <TaxonomyListingContainer {...taxonomyContainerProps} />}
      {showCurator && (
        <Curator
          isOpen={isCuratorOpen}
          onProductClick={redirectToPDP}
          onToggle={handleToggle}
          showFavourties
        />
      )}
    </>
  );
};

export default ShopListingPage;
